// Deferred Sentry init. Called by main.tsx AFTER `root.render` (via
// requestIdleCallback) so `@sentry/react` — dynamically imported here —
// stays off the render-critical entry chunk and no longer blocks first
// paint. The trade-off is that first-paint navigation tracing starts a
// beat late; errors thrown before the SDK loads are buffered by
// `sentry-client` and flushed on `bindSentry`.
//
// This is the ONLY module that reads `import.meta.env`, so it is pulled
// in exclusively by main.tsx and never by test files (which import the
// runtime-dependency-free `sentry-client` facade instead).
import { bindSentry, disableSentry } from './sentry-client';
import {
  shouldDropSentryEvent,
  type SentryDroppableError,
} from './sentry-event-filter';

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  // Local dev never reports to the shared issue stream. Any developer with
  // VITE_SENTRY_DSN in their .env was otherwise shipping every hot-reload
  // mistake to the same project that carries production incidents — a
  // mid-edit save that HMR reloads (a hook used before its import lands, say)
  // is not an incident, but it lands as one and can even auto-escalate to
  // High, drowning the real signal.
  //
  // Set VITE_SENTRY_ENABLE_DEV=true to opt in when you are deliberately
  // testing the Sentry wiring itself.
  const enabledInDev = import.meta.env.VITE_SENTRY_ENABLE_DEV === 'true';
  if (!dsn || (import.meta.env.DEV && !enabledInDev)) {
    disableSentry();
    return;
  }

  const Sentry = await import('@sentry/react');

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
      import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Record DOM + network only when an error fires — 0% sessions,
      // 100% on-error means every crash carries a replay while staying
      // well under Sentry's free-tier 50 replays/month cap.
      Sentry.replayIntegration({
        maskAllInputs: true,
        maskAllText: false,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: Number(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'
    ),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    // Filter noisy known-safe errors that pollute the issue list
    // without carrying signal (browser extension noise, ResizeObserver
    // loops, cancelled requests). Add more as ops discovers them.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Non-Error promise rejection captured',
      'Network request failed',
      // Sentry NEOWEB-7 — dynamic chunk 404 after a deploy. `importWithRetry`
      // already handles this by hard-reloading the tab once; the error
      // Sentry records is just the transitional failure. No user-visible
      // bug — filter out to keep the issue list signal-heavy.
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'Unable to load page',
      // Sentry NEOWEB-H — vite-plugin-pwa's registerSW/periodic update
      // rejects when the `sw.js` script fetch fails mid-deploy or on a
      // flaky network. Benign: the browser re-checks and updates the SW on
      // the next navigation/interval. No user-visible bug — filter the
      // transitional failure to keep the issue list signal-heavy.
      'Failed to update a ServiceWorker',
      'Failed to register a ServiceWorker',
      'An unknown error occurred when fetching the script',
    ],
    // Enrich the default 'ui.click' breadcrumb with data-testid / id /
    // aria-label / textContent so the timeline reads "cobrar-button"
    // instead of "div.flex.flex-col > button". Falls back to Sentry's
    // default CSS selector when none of the attributes match.
    // Drop EXPECTED session-expiry rejections. The api.service 401 path
    // flags the ApiError with `isAuthSessionExpired` right after it forces a
    // logout — the user is being signed out on purpose, so a background
    // caller's uncaught rejection is not a bug (this was Sentry NEOWEB-4,
    // "Object captured as promise rejection with keys: …"). Genuine errors
    // (incl. real 401 bugs that don't log out) carry no flag and still report.
    beforeSend(event, hint) {
      // Expected-noise drop rules (auth-expiry, transient network, already-
      // toasted business errorCodes) live in the Jest-safe predicate so each
      // one carries a regression test. See sentry-event-filter.ts.
      const ex = hint?.originalException as SentryDroppableError | undefined;
      if (shouldDropSentryEvent(ex)) {
        return null;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === 'ui.click' && hint?.event) {
        const target = hint.event.target as HTMLElement | null;
        if (target) {
          const testId = target
            .closest('[data-testid]')
            ?.getAttribute('data-testid');
          const id = target.id || target.closest('[id]')?.getAttribute('id');
          const aria = target.getAttribute('aria-label');
          const text = (target.textContent ?? '').trim().slice(0, 60);
          const label = testId || id || aria || text;
          if (label) {
            breadcrumb.message = `${breadcrumb.message ?? ''} [${label}]`;
          }
        }
      }
      return breadcrumb;
    },
  });

  bindSentry(Sentry);
}
